import type { WorkflowInput, WorkflowNodeConnector } from "@/stores/workflowsStore";
import type { Edge, Node } from "@xyflow/react";

import {
  calculateApiNodeId,
  computeDisplayLabel,
  CONNECTOR_VARIABLE_FIELDS,
  findAllForEachAncestors,
  findNonParentConnectorNodes,
  findParentNodes,
  generateX402FallbackVariablesFlat,
  getVariableCategory,
  WORKFLOW_ID_VAR,
  type ExtractNestedVariablesOptions,
  type NodeDefinition,
  type PayloadWithX402,
  type SchemaProperty,
  type WorkflowVariable,
} from "./extract-workflow-variables";

// ============================================================================
// FLAT VARIABLE EXTRACTION (for legacy variable picker)
// ============================================================================

/**
 * Extract template-local variables from a format action's data field.
 * The data field can be:
 * - A JSON object: {"price": "{{root.result.price}}", "name": "some value"} -> @price, @name
 * - A JSON string that parses to an object: same as above
 * - A variable reference: "{{node.result}}" -> no template variables (dynamic)
 */
function extractTemplateLocalVariables(dataValue: unknown): WorkflowVariable[] {
  const variables: WorkflowVariable[] = [];

  // Try to extract keys from the data
  let dataObj: Record<string, unknown> | null = null;

  if (typeof dataValue === "object" && dataValue !== null && !Array.isArray(dataValue)) {
    // Direct object
    dataObj = dataValue as Record<string, unknown>;
  } else if (typeof dataValue === "string") {
    // Try to parse as JSON object
    const trimmed = dataValue.trim();
    if (trimmed.startsWith("{") && !trimmed.startsWith("{{")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
          dataObj = parsed as Record<string, unknown>;
        }
      } catch {
        // Not valid JSON, skip
      }
    }
  }

  // Extract keys as template variables
  if (dataObj) {
    Object.keys(dataObj).forEach(key => {
      variables.push({
        id: `{{@${key}}}`,
        label: `Template field: ${key}`,
        category: "Template Variables",
        nodeId: "template",
        nodeName: "Template",
      });
    });
  }

  return variables;
}

/**
 * Recursively extract variables from nested object properties.
 * Supports both static category and dynamic category lookup via VARIABLE_GROUPING_RULES.
 *
 * @param options.category - Use this for static category assignment
 * @param options.nodeType + options.rootFieldName - Use these for dynamic category lookup
 */
export function extractNestedVariablesFlat(options: ExtractNestedVariablesOptions): WorkflowVariable[] {
  const { basePath, properties, nodeId, nodeName, maxDepth = 5, currentDepth = 0 } = options;

  if (currentDepth >= maxDepth) return [];

  // Determine category: use static category if provided, otherwise lookup dynamically
  const category =
    options.category ??
    (options.nodeType && options.rootFieldName
      ? getVariableCategory(options.nodeType, nodeName, options.rootFieldName)
      : nodeName);

  const variables: WorkflowVariable[] = [];

  Object.entries(properties).forEach(([fieldName, fieldSchema]) => {
    const fieldPath = `${basePath}.${fieldName}`;

    // Add the field itself
    variables.push({
      id: `{{${fieldPath}}}`,
      label: fieldSchema.description || fieldName,
      category,
      nodeId,
      nodeName,
    });

    // If this field has nested properties, recurse
    // Note: JSON Schema allows omitting "type" when "properties" is defined
    if (fieldSchema.properties) {
      variables.push(
        ...extractNestedVariablesFlat({
          ...options,
          basePath: fieldPath,
          properties: fieldSchema.properties,
          category, // Pass resolved category to children
          currentDepth: currentDepth + 1,
        }),
      );
    }
  });

  return variables;
}

/**
 * Build effective result schema for a node, applying outputSchema override
 * for code-transform nodes. This allows downstream nodes to see rich
 * variable suggestions based on the declared output structure.
 */
export function getEffectiveResultSchema(
  node: Node | undefined,
  nodeDefinition: NodeDefinition | undefined,
): NodeDefinition["resultSchema"] | undefined {
  const nodeResultSchema = (node?.data as Record<string, unknown>)?.resultSchema as
    | NodeDefinition["resultSchema"]
    | undefined;
  const baseSchema = nodeResultSchema || nodeDefinition?.resultSchema;

  const nodeType = (node?.data?.type as string) || "";

  if (nodeType === "code-transform" && baseSchema?.properties) {
    const payload = node?.data?.payload as Record<string, unknown> | undefined;
    const outputSchema = (payload?.outputType ?? payload?.outputSchema) as Record<string, unknown> | undefined;
    if (outputSchema) {
      return {
        ...baseSchema,
        properties: {
          ...baseSchema.properties,
          output: outputSchema as SchemaProperty,
        },
      };
    }
  }

  return baseSchema;
}

/**
 * Get output variables for a specific node type
 */
function getNodeOutputVariables(
  nodeId: string,
  nodeType: string,
  nodeName: string,
  nodeDefinition?: NodeDefinition,
  node?: Node,
): WorkflowVariable[] {
  const variables: WorkflowVariable[] = [];

  // Helper to get category for a result path (uses grouping rules if defined)
  const getCategoryForPath = (resultPath: string) => getVariableCategory(nodeType, nodeName, resultPath);

  // Check for node-specific result schema first (e.g., x402 endpoints store their own schema)
  // This takes precedence over the generic nodeDefinition schema
  const effectiveResultSchema = getEffectiveResultSchema(node, nodeDefinition);

  // If we have a result schema, extract variables from it
  if (effectiveResultSchema?.properties) {
    const resultProperties = effectiveResultSchema.properties;

    Object.entries(resultProperties).forEach(([fieldName, fieldSchema]) => {
      const fieldCategory = getCategoryForPath(fieldName);

      // Build label for the field
      // If field has additionalProperties but no explicit properties, add hint about nested access
      let fieldLabel = fieldSchema.description || fieldName;
      if (fieldSchema.type === "object" && fieldSchema.additionalProperties && !fieldSchema.properties) {
        // Field has dynamic content - hint that users can access nested fields
        fieldLabel = `${fieldLabel} (access nested fields with .fieldName)`;
      }

      // Create a variable for each result field
      variables.push({
        id: `{{${nodeId}.result.${fieldName}}}`,
        label: fieldLabel,
        category: fieldCategory,
        nodeId,
        nodeName,
      });

      // If this field is an object with properties, extract nested variables
      if (fieldSchema.type === "object" && fieldSchema.properties) {
        variables.push(
          ...extractNestedVariablesFlat({
            basePath: `${nodeId}.result.${fieldName}`,
            properties: fieldSchema.properties,
            nodeId,
            nodeName,
            nodeType,
            rootFieldName: fieldName,
          }),
        );
      }

      // If this field is an array with object items, extract item properties
      if (fieldSchema.type === "array" && fieldSchema.items?.properties) {
        variables.push(
          ...extractNestedVariablesFlat({
            basePath: `${nodeId}.result.${fieldName}[0]`,
            properties: fieldSchema.items.properties,
            nodeId,
            nodeName,
            nodeType,
            rootFieldName: fieldName,
          }),
        );
      }

      // For webhook triggers with x402 input schema, add nested body fields
      if (fieldName === "body" && node) {
        const x402Config = (node.data.payload as PayloadWithX402)?.x402;
        const inputSchema = x402Config?.discoveryInput?.schema;
        if (inputSchema?.properties) {
          Object.entries(inputSchema.properties).forEach(([inputFieldName, inputFieldSchema]) => {
            variables.push({
              id: `{{${nodeId}.result.body.${inputFieldName}}}`,
              label: inputFieldSchema.description || inputFieldName,
              category: fieldCategory,
              nodeId,
              nodeName,
            });
          });
        }
      }
    });
  } else if (nodeType === "call-x402-endpoint") {
    // Fallback for call-x402-endpoint nodes without a schema
    // Uses shared constants to ensure consistency with hierarchical extraction
    variables.push(...generateX402FallbackVariablesFlat(nodeId, nodeName, getCategoryForPath));
  } else {
    // Fallback to generic variables if no result schema is available
    // This helps with backwards compatibility and custom nodes
    variables.push(
      {
        id: `{{${nodeId}.result.value}}`,
        label: "Result value",
        category: nodeName,
        nodeId,
        nodeName,
      },
      {
        id: `{{${nodeId}.result.data}}`,
        label: "Result data",
        category: nodeName,
        nodeId,
        nodeName,
      },
    );
  }

  // All nodes always have the full result object available
  // (except manual triggers — their result only contains triggeredAt)
  if (nodeType !== "manual") {
    variables.push({
      id: `{{${nodeId}.result}}`,
      label: "Full result object",
      category: nodeName,
      nodeId,
      nodeName,
    });
  }

  // Extract payload variables from the node's configured payload
  // These allow referencing configuration values from parent nodes (e.g., {{root.payload.rrule}})
  // Only extract if the node has actual payload data configured
  const nodePayload = node?.data.payload as Record<string, unknown> | undefined;
  if (nodePayload) {
    const payloadCategory = `${nodeName} (Config)`;
    Object.entries(nodePayload).forEach(([fieldName, fieldValue]) => {
      // Skip complex objects and null/undefined values - only expose primitive values
      // that are commonly referenced (strings, numbers, booleans)
      if (fieldValue === null || fieldValue === undefined) return;
      // Skip schema metadata fields and block-internal fields
      if (
        ["inputSchema", "outputSchema", "inputType", "outputType", "blockId", "blockVersion", "inputMapping"].includes(
          fieldName,
        )
      )
        return;
      if (typeof fieldValue === "object" && !Array.isArray(fieldValue)) {
        // For objects, expose the whole object but not individual nested fields
        // to avoid cluttering the variable picker
        variables.push({
          id: `{{${nodeId}.payload.${fieldName}}}`,
          label: `Config: ${fieldName}`,
          category: payloadCategory,
          nodeId,
          nodeName,
        });
        return;
      }
      variables.push({
        id: `{{${nodeId}.payload.${fieldName}}}`,
        label: `Config: ${fieldName}`,
        category: payloadCategory,
        nodeId,
        nodeName,
      });
    });
  }

  // Extract connector variables if the node has a connector with a known type
  const connectorType = (node?.data.connector as WorkflowNodeConnector | undefined)?.type;
  if (connectorType) {
    const fields = CONNECTOR_VARIABLE_FIELDS[connectorType];
    if (fields) {
      const connectorCategory = `${nodeName} (Connector)`;
      for (const field of fields) {
        variables.push({
          id: `{{${nodeId}.connector.${field.name}}}`,
          label: `Connector: ${field.description}`,
          category: connectorCategory,
          nodeId,
          nodeName,
        });
      }
    }
  }

  return variables;
}

/**
 * Extract available variables from workflow nodes
 * Variables come from parent nodes' outputs
 */
export function extractWorkflowVariables(
  currentNodeId: string,
  allNodes: Node[],
  allEdges: Edge[],
  nodeDefinitions?: NodeDefinition[],
  cache?: Map<string, WorkflowVariable[]>,
  inputSchema?: WorkflowInput[],
  stepNumbers?: Map<string, number>,
): WorkflowVariable[] {
  const variables: WorkflowVariable[] = [];

  // Find the current node to check for template-local variables
  const currentNode = allNodes.find(n => n.id === currentNodeId);

  // If current node is a format action, extract template-local variables from its data field
  // These are variables prefixed with @ that reference keys in the data object
  if (currentNode && currentNode.data.type === "format") {
    const payload = currentNode.data.payload as { data?: unknown } | undefined;
    const dataValue = payload?.data;

    if (dataValue) {
      const templateVars = extractTemplateLocalVariables(dataValue);
      variables.push(...templateVars);
    }
  }

  // Find all parent nodes (nodes that come before this one in the tree)
  const parentNodeIds = findParentNodes(currentNodeId, allEdges);

  // Check if this node is inside a for-each loop (any ancestor is a for-each node)
  // If so, add loop variables ($item, $index, and nested for-each variables) to available variables
  const forEachAncestors = findAllForEachAncestors(currentNodeId, allNodes, allEdges);
  if (forEachAncestors.length > 0) {
    // Innermost → $item, $index (same as before)
    const innermost = forEachAncestors[0];
    const innermostPayload = innermost.data.payload as { itemVar?: string; indexVar?: string } | undefined;
    const itemVar = innermostPayload?.itemVar || "$item";
    const indexVar = innermostPayload?.indexVar || "$index";

    variables.push({
      id: `{{${itemVar}}}`,
      label: "Current loop item",
      category: "Loop Variables",
      nodeId: innermost.id,
      nodeName: (innermost.data.name as string) || "for-each",
    });

    variables.push({
      id: `{{${indexVar}}}`,
      label: "Current loop index (0-based)",
      category: "Loop Variables",
      nodeId: innermost.id,
      nodeName: (innermost.data.name as string) || "for-each",
    });

    // Depth >= 2 → $parent_item, $parent_index
    if (forEachAncestors.length >= 2) {
      const parent = forEachAncestors[1];
      const parentName = (parent.data.name as string) || "for-each";
      variables.push(
        {
          id: "{{$parent_item}}",
          label: `Parent loop item (${parentName})`,
          category: "Loop Variables",
          nodeId: parent.id,
          nodeName: parentName,
        },
        {
          id: "{{$parent_index}}",
          label: `Parent loop index (${parentName})`,
          category: "Loop Variables",
          nodeId: parent.id,
          nodeName: parentName,
        },
      );
    }

    // Outer ancestors → $for.{apiNodeId}.item/index (skip innermost, already covered by $item/$index)
    for (let i = 1; i < forEachAncestors.length; i++) {
      const ancestor = forEachAncestors[i];
      const apiNodeId = calculateApiNodeId(ancestor, allNodes, allEdges);
      const name = (ancestor.data.name as string) || "for-each";
      variables.push(
        {
          id: `{{$for.${apiNodeId}.item}}`,
          label: `Loop item (${name})`,
          category: "Loop Variables",
          nodeId: ancestor.id,
          nodeName: name,
        },
        {
          id: `{{$for.${apiNodeId}.index}}`,
          label: `Loop index (${name})`,
          category: "Loop Variables",
          nodeId: ancestor.id,
          nodeName: name,
        },
      );
    }
  }

  // For each parent node, extract its output variables
  parentNodeIds.forEach(nodeId => {
    // Check cache first to avoid redundant computations
    if (cache?.has(nodeId)) {
      variables.push(...cache.get(nodeId)!);
      return;
    }

    const node = allNodes.find(n => n.id === nodeId);
    if (!node) return;

    const nodeType = (node.data.type as string) || "";
    const nodeName = (node.data.name as string) || nodeType;

    // Calculate the API node ID (this is what will be used in the actual workflow execution)
    const apiNodeId = calculateApiNodeId(node, allNodes, allEdges);

    // Get the node definition to extract result schema.
    // For block nodes, look up by "block:${blockId}" to find the synthetic definition
    // created by useBlockDefinitions (each block has a unique outputSchema).
    const definitionLookupType =
      nodeType === "block" ? `block:${(node.data.payload as Record<string, unknown>)?.blockId || ""}` : nodeType;
    const nodeDefinition = nodeDefinitions?.find(def => def.type === definitionLookupType);

    // Extract output variables based on result schema and node data
    const nodeVariables = getNodeOutputVariables(apiNodeId, nodeType, nodeName, nodeDefinition, node);

    // Populate displayLabel on each variable if stepNumbers are provided
    if (stepNumbers) {
      const stepNum = stepNumbers.get(nodeId);
      for (const v of nodeVariables) {
        v.displayLabel = computeDisplayLabel(v.id, nodeName, stepNum);
      }
    }

    // Store in cache for reuse
    if (cache) {
      cache.set(nodeId, nodeVariables);
    }

    variables.push(...nodeVariables);
  });

  // Extract connector variables from non-parent nodes (e.g., downstream connector
  // nodes whose wallet address should be referenceable from upstream nodes).
  for (const cn of findNonParentConnectorNodes(currentNodeId, parentNodeIds, allNodes, allEdges, stepNumbers)) {
    const connectorCategory = `${cn.nodeName} (Connector)`;
    for (const field of cn.fields) {
      const varId = `{{${cn.apiNodeId}.connector.${field.name}}}`;
      variables.push({
        id: varId,
        label: `Connector: ${field.description}`,
        category: connectorCategory,
        nodeId: cn.apiNodeId,
        nodeName: cn.nodeName,
        displayLabel: stepNumbers ? computeDisplayLabel(varId, cn.nodeName, cn.stepNum) : undefined,
      });
    }
  }

  // Add input schema variables (declared workflow inputs appear as {{root.input.<key>}})
  if (inputSchema && inputSchema.length > 0) {
    // Find the root trigger node to get its name for the category
    const incomingEdgeTargets = new Set(allEdges.map(e => e.target));
    const rootNode = allNodes.find(n => !incomingEdgeTargets.has(n.id));
    const rootName = rootNode
      ? (rootNode.data.name as string) || (rootNode.data.type as string) || "Trigger"
      : "Trigger";

    // Input schema fields use the "input" section (e.g., {{root.input.userId}})
    // to differentiate from trigger result fields (e.g., {{root.result.triggeredAt}})
    const rootStepNum = rootNode ? stepNumbers?.get(rootNode.id) : undefined;
    inputSchema.forEach(input => {
      if (!input.key) return;
      const varId = `{{root.input.${input.key}}}`;
      variables.push({
        id: varId,
        label: input.description || `Input: ${input.key}`,
        category: `${rootName} - Inputs`,
        nodeId: "root",
        nodeName: rootName,
        displayLabel: stepNumbers ? computeDisplayLabel(varId, rootName, rootStepNum) : undefined,
      });
    });
  }

  // Add system variables (always available)
  variables.push({
    id: WORKFLOW_ID_VAR,
    label: "Current workflow ID",
    category: "System",
    nodeId: "$system",
    nodeName: "System",
    displayLabel: "Workflow ID",
  });

  return variables;
}
